import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Drawer,
  IconButton, Link, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon, Close as CloseIcon, Refresh as RefreshIcon, Send as SendIcon, SupportAgent as SupportAgentIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import {
  CONTACT_EMAIL, KindChip, MessageBubble, REQUEST_CATEGORIES, SUPPORT_EMAIL, TicketStatusChip, fieldErrorsFrom, relativeTime,
  requesterFromUser, submitErrorMessage,
} from '../components/Support/supportUi';

const emptyRequest = { kind: 'support', subject: '', message: '' };

const SupportCenter = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [newOpen, setNewOpen] = useState(false);
  const [request, setRequest] = useState(emptyRequest);
  const [requestErrors, setRequestErrors] = useState({});
  const [requestError, setRequestError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);

  const [active, setActive] = useState(null); // ticket in the thread drawer
  const [threadLoading, setThreadLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [replying, setReplying] = useState(false);
  const [threadError, setThreadError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.support.getMine();
      setTickets(res.data.tickets || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load your support tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upsert = (ticket) => setTickets((prev) => {
    const rest = prev.filter((t) => t._id !== ticket._id);
    return [ticket, ...rest];
  });

  const openNew = () => {
    setRequest(emptyRequest);
    setRequestErrors({});
    setRequestError(null);
    setNewOpen(true);
  };

  const createRequest = async () => {
    const local = {};
    if (!request.subject.trim()) local.subject = 'Subject is required';
    if (request.message.trim().length < 10) local.message = 'Please describe your request (at least 10 characters)';
    if (Object.keys(local).length) { setRequestErrors(local); return; }
    try {
      setCreating(true);
      setRequestError(null);
      const who = requesterFromUser(user);
      const payload = { kind: request.kind, name: who.name || who.email, email: who.email, subject: request.subject.trim(), message: request.message.trim() };
      if (who.company) payload.company = who.company;
      const res = await apiService.support.submit(payload);
      setNewOpen(false);
      setCreated(res.data.ticket?.number || null);
      await load();
    } catch (err) {
      setRequestErrors(fieldErrorsFrom(err));
      setRequestError(submitErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const openThread = async (ticket) => {
    setActive(ticket);
    setReply('');
    setThreadError(null);
    try {
      setThreadLoading(true);
      const res = await apiService.support.getById(ticket._id);
      setActive(res.data.ticket);
      upsert(res.data.ticket);
    } catch (err) {
      setThreadError(err?.response?.data?.message || 'Failed to load the conversation');
    } finally {
      setThreadLoading(false);
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    try {
      setReplying(true);
      setThreadError(null);
      const res = await apiService.support.addMessage(active._id, reply.trim());
      setActive(res.data.ticket);
      upsert(res.data.ticket);
      setReply('');
    } catch (err) {
      setThreadError(err?.response?.data?.message || 'Failed to send your reply');
    } finally {
      setReplying(false);
    }
  };

  const setReq = (key) => (e) => {
    const value = e.target.value;
    setRequest((r) => ({ ...r, [key]: value }));
    if (requestErrors[key]) setRequestErrors((fe) => ({ ...fe, [key]: undefined }));
  };

  const messages = [...(active?.messages || [])].sort((a, b) => new Date(a.at) - new Date(b.at));

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>Support</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Requests from your organisation to the Excubia team. We usually reply within one business day.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Refresh"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>New request</Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {created && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCreated(null)}>Request {created} submitted. We&apos;ve emailed you a confirmation.</Alert>}

      <Card>
        {loading && tickets.length === 0 ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : tickets.length === 0 ? (
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <SupportAgentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6">No support requests yet</Typography>
            <Typography color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              Use <strong>New request</strong> above, the <Link component={RouterLink} to="/support">public support page</Link>, or email{' '}
              <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>. For partnerships write to{' '}
              <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>.
            </Typography>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={openNew}>New request</Button>
          </CardContent>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ticket</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last activity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.map((t) => (
                  <TableRow key={t._id} hover sx={{ cursor: 'pointer' }} onClick={() => openThread(t)}>
                    <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{t.number}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{t.subject}</Typography>
                      {t.requester?.name && <Typography variant="caption" color="text.secondary">by {t.requester.name}</Typography>}
                    </TableCell>
                    <TableCell><KindChip kind={t.kind} /></TableCell>
                    <TableCell><TicketStatusChip status={t.status} lastMessageFrom={t.lastMessageFrom} requesterView /></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={t.lastMessageAt ? new Date(t.lastMessageAt).toLocaleString() : ''}>
                        <span>{relativeTime(t.lastMessageAt || t.createdAt)}</span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* New request */}
      <Dialog open={newOpen} onClose={() => !creating && setNewOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New support request</DialogTitle>
        <DialogContent>
          {requestError && <Alert severity="error" sx={{ mb: 2 }}>{requestError}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Replies go to <strong>{user?.email}</strong>.
          </Typography>
          <Stack spacing={2}>
            <TextField select label="Category" value={request.kind} onChange={setReq('kind')} fullWidth>
              {REQUEST_CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </TextField>
            <TextField label="Subject" value={request.subject} onChange={setReq('subject')} required fullWidth error={Boolean(requestErrors.subject)} helperText={requestErrors.subject} inputProps={{ maxLength: 200 }} />
            <TextField label="Message" value={request.message} onChange={setReq('message')} required fullWidth multiline minRows={5} error={Boolean(requestErrors.message)} helperText={requestErrors.message} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOpen(false)} disabled={creating}>Cancel</Button>
          <Button variant="contained" onClick={createRequest} disabled={creating} startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}>Submit</Button>
        </DialogActions>
      </Dialog>

      {/* Thread */}
      <Drawer anchor="right" open={Boolean(active)} onClose={() => setActive(null)} PaperProps={{ sx: { width: { xs: '100%', sm: 560 } } }}>
        {active && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{active.number}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>{active.subject}</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <KindChip kind={active.kind} />
                  <TicketStatusChip status={active.status} lastMessageFrom={active.lastMessageFrom} requesterView />
                </Stack>
              </Box>
              <IconButton onClick={() => setActive(null)} aria-label="Close"><CloseIcon /></IconButton>
            </Box>
            <Divider />
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
              {threadLoading && messages.length === 0 && <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>}
              {messages.map((m) => (
                <MessageBubble
                  key={m.id || m.at}
                  mine={m.from === 'requester'}
                  author={m.from === 'agent' ? `Excubia support${m.authorName ? ` · ${m.authorName}` : ''}` : (m.authorName || 'You')}
                  body={m.body}
                  at={m.at}
                />
              ))}
            </Box>
            <Divider />
            <Box sx={{ p: 2 }}>
              {threadError && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setThreadError(null)}>{threadError}</Alert>}
              {active.status === 'closed' ? (
                <Typography variant="body2" color="text.secondary">
                  This ticket is closed. Open a new request if you need more help.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  <TextField
                    multiline minRows={3} maxRows={10} fullWidth placeholder="Write a reply…" value={reply} onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) sendReply(); }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Ctrl+Enter to send</Typography>
                    <Button variant="contained" onClick={sendReply} disabled={replying || !reply.trim()} startIcon={replying ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}>
                      Send reply
                    </Button>
                  </Box>
                </Stack>
              )}
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default SupportCenter;
